function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { INPUT, LABEL } from "../../components/form-styles";
import { withRouter } from "react-router";
import { CssContainer } from "../../components/container";
import { Link } from "react-router-dom";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import PlunkDropDown from "../../components/plunk-dropdown";
import SenderPicker from "../email-campaigns/sender-picker";
import ServiceFactory from "../../service";
import account from "../../data/account";
import { hasTenantFeature } from "../../functions/tenant-features";
import { loadBrandKit, BRAND_MERGE_FIELDS } from "../../functions/brand-kit";
import { emailSvc } from "../email-automation/common";
import Composer, { starterDesign, parseDesign, emptyDesign } from "../email-automation/composer";
import { SubjectSuggest } from "../email-automation/ai-draft";
import ContentPickList, { CONTENT_PICK_TOP } from "../email-automation/content-pick-list";
import { authHeaders, apiRoot, errText } from "../reports/common";
import { api, audienceApi, normalizeLists, ago, idStr } from "../email-campaigns/api";
import { journeySvc, TRIGGER_KINDS, CONDITION_KINDS, GOAL_KINDS, TZ_OPTIONS, emptyGraph, insertNode, updateNode, nodeById, utcHourLocal, emailNodes, triggerText, LAPSE_RISK_LEVELS, removeNode, moveNode, parentSlotOf, sameSlot, slotTarget, nodeTitle, delayText, conditionText, tagListName, isSingleJourney } from "./common";

// Content Hub picker (CONTENT_HUB_NEXT §6): link an email NODE to a
// content_container so the journey send credits that piece's funnel.
function contentSvc(api) {
  const Service = ServiceFactory.getType(`/content${api}`, "product"); // /v1 root
  return new Service();
}

/*
 * Journey builder (EMAIL_JOURNEYS_UX.md; 2026-10-09 redesign = JourneyEdit mockup).
 * Top bar (name, state, autosave, test, Turn on) · the journey as a vertical
 * step map in the middle (start card, emails, waits, splits, + choosers) ·
 * a right panel with the selected node's settings (the start node holds the
 * trigger, lists, reach, sending rules and goal). New journeys open on a
 * template picker. Email steps use the section composer; the design is
 * persisted per node (designJson) and a rendered copy lands in emailBody so
 * the validator/legacy readers see a body. The worker re-renders from the
 * design per send (products, recommendations, brand kit).
 *
 * Running journeys are read-only server-side (409): the top bar offers
 * Pause, and editing resumes once the server confirms Paused.
 */

// Journey (API shape) → editor state. Used when editing and when starting from a saved template (2026-10-04).
function stateFromJourney(j, opts) {
  const asTemplate = !!(opts && opts.asTemplate);
  const tc = j.triggerConfig || {};
  const sc = j.senderConfig || {};
  const known = TRIGGER_KINDS.some(([k]) => k === j.triggerType && k !== "custom");
  const graph = j.graph && Array.isArray(j.graph.nodes) ? {
    settings: {},
    ...j.graph
  } : emptyGraph();
  graph.settings = {
    ...(graph.settings || {})
  };
  if (asTemplate) delete graph.settings.isTemplate;
  const designs = {};
  const bodyModes = {};
  graph.nodes.forEach(n => {
    if (n.type !== "email") return;
    const d = parseDesign(n.designJson);
    if (d) designs[n.id] = d;
    bodyModes[n.id] = d ? "compose" : n.emailBody ? "preview" : "compose";
  });
  const goal = j.goal || {};
  return {
    name: asTemplate ? "" : j.name || "",
    status: asTemplate ? "Draft" : j.status || "Draft",
    kind: known ? j.triggerType : "custom",
    eventName: known ? "" : tc.eventName || "",
    eventCategory: known ? "Engagement" : j.triggerType === "Conversion" ? "Conversion" : "Engagement",
    lookbackDays: tc.lookbackDays != null ? String(tc.lookbackDays) : "",
    excludeConverted: tc.excludeConverted !== false,
    lapseLevel: LAPSE_RISK_LEVELS.some(([k]) => k === String(tc.level || "").toLowerCase()) ? String(tc.level).toLowerCase() : "at_risk",
    include: (j.audienceInclude || []).map(idStr),
    exclude: (j.audienceExclude || []).map(idStr),
    // BigNumber → string: the chips compare by string
    fromName: sc.fromName || "",
    fromEmail: sc.fromEmail || "",
    graph,
    designs,
    bodyModes,
    template: asTemplate ? `saved:${j.id}` : "loaded",
    selected: null,
    composing: null,
    showPicker: false,
    isTemplate: !asTemplate && graph.settings.isTemplate === true,
    goalKind: goal.kind || "",
    goalEvent: goal.eventName || "",
    quietStart: graph.settings.quietStartHourUtc != null ? String(graph.settings.quietStartHourUtc) : "",
    quietEnd: graph.settings.quietEndHourUtc != null ? String(graph.settings.quietEndHourUtc) : "",
    quietTz: graph.settings.quietTimezone || "",
    dailyCap: graph.settings.dailyEmailCap != null ? String(graph.settings.dailyEmailCap) : ""
  };
}

// [key, title, what it does, steps]. Keys are stable: ?template=<key> links use them.
const TEMPLATES = [["welcome", "Welcome series", "Say hello when someone signs up, then follow up over the next week.", "3 emails over about a week"], ["browse", "Came back to look again", "Remind people about what they looked at, then an offer if they still haven't bought.", "Reminder, wait a day, offer"], ["cart", "Left something in the cart", "A reminder about their cart, then an offer a day later if they haven't bought.", "Reminder, wait a day, offer"], ["single", "Single email", "One email that goes out when something happens.", "1 email"], ["blank", "Start from scratch", "An empty journey. You add the steps.", ""]];
function templateGraph(key) {
  let g = emptyGraph();
  const designs = {};
  const add = (slot, type, patch, design) => {
    const r = insertNode(g, slot, type, patch);
    g = r.graph;
    if (design) designs[r.id] = design;
    return r.id;
  };
  const next = id => ({
    parentId: id,
    edge: "next"
  });
  let kind = "WelcomeSeries";
  if (key === "welcome") {
    const e1 = add(null, "email", {
      name: "Welcome",
      emailSubject: "Welcome, {{FirstName}}"
    }, starterDesign("WelcomeSeries"));
    const d1 = add(next(e1), "delay", {
      delayDays: 2
    });
    const e2 = add(next(d1), "email", {
      name: "Where to start",
      emailSubject: "A few places to start"
    }, starterDesign("custom"));
    const d2 = add(next(e2), "delay", {
      delayDays: 3
    });
    const b = add(next(d2), "branch", {
      name: "Opened the last email?",
      condition: {
        kind: "openedLastEmail",
        eventName: "",
        audienceId: "",
        windowDays: null
      }
    });
    add({
      parentId: b,
      edge: "yes"
    }, "email", {
      name: "Picked for you",
      emailSubject: "A few things we think you'll like"
    }, starterDesign("CustomerLoyalty"));
    add({
      parentId: b,
      edge: "no"
    }, "exit");
  } else if (key === "single") {
    // DS 2026-10-04: single emails go through the journey pipeline — one email step, no goal, trigger as chosen.
    add(null, "email", {
      name: "Your email",
      emailSubject: "Hello, {{FirstName}}"
    }, starterDesign("custom"));
  } else if (key === "browse" || key === "cart") {
    kind = key === "cart" ? "CartAbandoned" : "ProductViewed";
    const e1 = add(null, "email", key === "cart" ? {
      name: "Cart reminder",
      emailSubject: "Your cart is waiting, {{FirstName}}"
    } : {
      name: "Still thinking it over?",
      emailSubject: "Still thinking it over, {{FirstName}}?"
    }, starterDesign(kind));
    const d1 = add(next(e1), "delay", {
      delayDays: 1
    });
    const b = add(next(d1), "branch", {
      name: "Converted since?",
      condition: {
        kind: "converted",
        eventName: "",
        audienceId: "",
        windowDays: null
      }
    });
    add({
      parentId: b,
      edge: "yes"
    }, "exit");
    add({
      parentId: b,
      edge: "no"
    }, "email", {
      name: "An offer to help decide",
      emailSubject: "A little something to help you decide"
    }, {
      frame: "brand",
      version: 1,
      sections: [{
        type: "hero",
        headline: "Still on your mind, {{FirstName}}?",
        subhead: "Here's a little something to make the decision easier.",
        imageFrom: "firstProduct",
        label: "Take another look",
        url: ""
      }, {
        type: "products",
        source: "trigger",
        max: 3,
        columns: 3,
        showPrice: true,
        button: "View"
      }, {
        type: "offer",
        headline: "Free delivery this week",
        html: "<p>On your order this week. Mention this email or use the code at checkout.</p>",
        code: "",
        label: "",
        url: ""
      }]
    });
  }
  // Built-in goal (2026-10-04): browse / cart journeys exit on any conversion; a welcome series runs to the end.
  const goalKind = key === "browse" || key === "cart" ? "conversion" : "";
  return {
    graph: g,
    designs,
    kind,
    goalKind
  };
}

// Browse / cart journeys without a goal keep nudging people who already bought (step 5 hint, step 6 warning).
function wantsGoal(st) {
  return st.template === "browse" || st.template === "cart" || st.kind === "CartAbandoned" || st.kind === "ProductViewed";
}

// The editor state keys that make up the saved journey: a change to any of them marks the draft dirty
// (autosave) and, for the trigger/audience subset, re-estimates the expected reach.
const MODEL_KEYS = ["name", "kind", "eventName", "eventCategory", "lookbackDays", "excludeConverted", "lapseLevel", "include", "exclude", "fromName", "fromEmail", "graph", "designs", "bodyModes", "isTemplate", "goalKind", "goalEvent", "quietStart", "quietEnd", "quietTz", "dailyCap"];
const REACH_KEYS = ["kind", "eventName", "eventCategory", "lookbackDays", "excludeConverted", "lapseLevel", "include", "exclude"];
const SAVE_DEBOUNCE = 900;
const ESTIMATE_DEBOUNCE = 600;
const REACH_WINDOW_DAYS = 7;
function agoShort(d) {
  if (!d) return "";
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s} s ago`;
  return ago(d);
}

// Same markup as the campaign wizard's list picker (steps.jsx): list name, kind and member count, searchable.
class JourneyListPicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      q: ""
    });
  }
  render() {
    const {
      lists,
      chosen,
      onPick,
      onClose
    } = this.props;
    const q = this.state.q.trim().toLowerCase();
    const rows = (lists || []).filter(l => chosen.indexOf(l.id) < 0 && (!q || `${l.name} ${l.kind}`.toLowerCase().includes(q))).slice(0, 40);
    return /*#__PURE__*/React.createElement("div", {
      className: "ec-picker"
    }, /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      autoFocus: true,
      value: this.state.q,
      placeholder: "Search lists",
      onChange: e => this.setState({
        q: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Escape") onClose();
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "ec-picker-list"
    }, !lists ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "Loading lists\u2026") : !rows.length ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "No list matches.") : rows.map(l => /*#__PURE__*/React.createElement("button", {
      key: l.id,
      type: "button",
      onClick: () => onPick(l.id)
    }, /*#__PURE__*/React.createElement("span", null, l.name), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, l.kind, l.total ? ` · ${l.total.toLocaleString("en-US")}` : "")))), /*#__PURE__*/React.createElement("div", {
      className: "ec-picker-foot"
    }, /*#__PURE__*/React.createElement("a", {
      onClick: onClose
    }, "close"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: "/Audiences/this_month"
    }, "manage lists")));
  }
}
const MERGE_CHIPS = [["{{FirstName}}", "Contact first name"], ["{{Products}}", "Products from the trigger event"], ["{{UnsubscribeUrl}}", "Unsubscribe link (added for you if missing)"], ...BRAND_MERGE_FIELDS];
const HOURS = Array.from({
  length: 24
}, (_, h) => h);
function initialState(props) {
  return {
    ready: !props.params.id,
    notFound: false,
    name: "",
    kind: "WelcomeSeries",
    eventName: "",
    eventCategory: "Engagement",
    eventFreeText: false,
    lookbackDays: "",
    excludeConverted: true,
    lapseLevel: "at_risk",
    audienceList: null,
    include: [],
    exclude: [],
    picking: null,
    fromName: "",
    fromEmail: "",
    brand: null,
    brandPrefilled: false,
    graph: emptyGraph(),
    designs: {},
    bodyModes: {},
    selected: null,
    template: null,
    isTemplate: false,
    savedTemplates: [],
    goalKind: "",
    goalEvent: "",
    quietStart: "",
    quietEnd: "",
    quietTz: "",
    dailyCap: "",
    triggers: null,
    status: "Draft",
    createdHere: false,
    pausedHere: false,
    composing: null,
    showPicker: false,
    menu: null,
    waitEdit: null,
    confirmRemove: null,
    dragging: null,
    over: null,
    senderOpen: false,
    quietOpen: false,
    showMore: false,
    showHow: false,
    saving: false,
    error: null,
    issues: null,
    confirmActivate: false,
    pausing: false,
    saveState: "idle",
    savedAt: null,
    saveErr: null,
    estimate: null,
    estimating: false,
    estimateErr: null,
    estimateHidden: false,
    testBusy: null,
    testNote: null,
    contentPicks: null,
    contentPickerNode: null,
    contentTitles: {}
  };
}
class EmailJourneyEditorComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "autosave", opts => {
      clearTimeout(this._saveT);
      const flush = !!(opts && opts.flush);
      if (!flush && !this._alive) return Promise.resolve();
      if (this._saving) {
        this._pending = true;
        return this._saving;
      }
      if (!this.canAutosave()) return Promise.resolve();
      this._dirty = false;
      if (!flush) this.setState({
        saveState: "saving",
        saveErr: null
      });
      this._saving = (async () => {
        try {
          // Light save: the design is persisted as-is; the explicit Save renders bodies.
          const nodes = await this.buildNodes({
            render: false
          });
          const body = this.payload(nodes);
          let id = this.id();
          if (!id) {
            const created = await journeySvc().postAsync(body);
            id = created && created.id != null ? String(created.id) : null;
            if (!id) throw new Error("Create returned no id");
            this._createdId = id;
            if (this._alive) {
              this.setState({
                createdHere: true,
                status: created && created.status || "Draft"
              });
              this.props.history.replace(`/Email/journeys/edit/${id}`);
            }
          } else {
            await journeySvc(`/${id}`).putAsync(body);
          }
          if (this._alive) this.setState({
            saveState: this._dirty ? "dirty" : "saved",
            savedAt: new Date(),
            saveErr: null
          });
        } catch (e) {
          if (this._alive) this.setState({
            saveState: "error",
            saveErr: errText(e, "Autosave failed.")
          });
        } finally {
          this._saving = null;
          if (this._pending) {
            this._pending = false;
            if (this._alive) this.queueSave();
          }
        }
      })();
      return this._saving;
    });
    _defineProperty(this, "estimate", async () => {
      const st = this.state;
      if (!this._alive || st.estimateHidden) return;
      if (st.kind === "custom" && !st.eventName.trim()) {
        this.setState({
          estimate: null,
          estimating: false,
          estimateErr: null
        });
        return;
      }
      const p = this.payload([]);
      const seq = ++this._estSeq;
      this.setState({
        estimating: true,
        estimateErr: null
      });
      try {
        const d = await api("POST", "/journeys/estimate", {
          triggerType: p.triggerType,
          triggerConfig: p.triggerConfig,
          audienceInclude: (st.include || []).map(String),
          audienceExclude: (st.exclude || []).map(String)
        });
        if (!this._alive || seq !== this._estSeq) return;
        this.setState({
          estimate: d || null,
          estimating: false
        });
      } catch (e) {
        if (!this._alive || seq !== this._estSeq) return;
        if (e && (e.status === 404 || e.status === 405 || e.status === 501)) {
          this.setState({
            estimateHidden: true,
            estimating: false,
            estimate: null
          });
          return;
        }
        this.setState({
          estimating: false,
          estimateErr: errText(e, "Couldn't estimate reach right now.")
        });
      }
    });
    _defineProperty(this, "renderPreview", async (design, opts) => {
      const body = {
        design,
        firstName: "Dana"
      };
      if (opts && opts.personId) {
        body.personId = opts.personId;
        delete body.firstName;
      }
      const res = await fetch(`${apiRoot()}/campaign/email/render`, {
        method: "POST",
        headers: {
          ...authHeaders(),
          "content-type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Preview failed (${res.status}).`);
      return d;
    });
    // "Send test to me": the server renders the step exactly like the preview
    // (sample products, brand frame) and sends it to the signed-in user now.
    _defineProperty(this, "sendTest", async n => {
      const st = this.state;
      const me = account.getEmail && account.getEmail() || "";
      const body = {
        subject: n.emailSubject || "",
        email: me || null,
        fromName: st.fromName,
        fromEmail: st.fromEmail,
        firstName: "Dana"
      };
      if (this.bodyMode(n.id) === "compose") {
        const design = st.designs[n.id];
        if (!design || !(design.sections || []).length) {
          this.setState({
            testNote: {
              id: n.id,
              kind: "error",
              text: "Add at least one section first."
            }
          });
          return;
        }
        body.design = design;
      } else {
        if (!(n.emailBody || "").trim()) {
          this.setState({
            testNote: {
              id: n.id,
              kind: "error",
              text: "The HTML body is empty."
            }
          });
          return;
        }
        body.html = n.emailBody;
      }
      this.setState({
        testBusy: n.id,
        testNote: null
      });
      try {
        const res = await fetch(`${apiRoot()}/campaign/email/test-send`, {
          method: "POST",
          headers: {
            ...authHeaders(),
            "content-type": "application/json"
          },
          body: JSON.stringify(body)
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error || `Test send failed (${res.status}).`);
        if (this._alive) this.setState({
          testBusy: null,
          testNote: {
            id: n.id,
            kind: "info",
            text: `Sent to ${d.to || me || "you"}: subject "${d.subject || body.subject}".`
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          testBusy: null,
          testNote: {
            id: n.id,
            kind: "error",
            text: e && e.message || "Test send failed."
          }
        });
      }
    });
    _defineProperty(this, "pickSavedTemplate", async id => {
      try {
        const j = await journeySvc(`/${id}`).getAsync();
        if (!this._alive || !j) return;
        const next = stateFromJourney(j, {
          asTemplate: true
        });
        this.setState({
          ...next,
          name: this.state.name || "",
          selected: null,
          composing: null,
          showPicker: false
        });
      } catch (e) {/* keep the current draft */}
    });
    _defineProperty(this, "pickTemplate", key => {
      const {
        graph,
        designs,
        kind,
        goalKind
      } = templateGraph(key);
      const bodyModes = {};
      Object.keys(designs).forEach(id => {
        bodyModes[id] = "compose";
      });
      this.setState({
        graph,
        designs,
        bodyModes,
        kind: key === "blank" || key === "single" ? this.state.kind : kind,
        template: key,
        selected: null,
        composing: null,
        showPicker: false,
        menu: null,
        waitEdit: null,
        goalKind: key === "blank" ? this.state.goalKind : goalKind
      });
    });
    _defineProperty(this, "loadContentPicks", async () => {
      const d = await contentSvc(`/containers/this_quarter?$top=${CONTENT_PICK_TOP}`).getAsync().catch(() => null);
      if (!this._alive) return;
      const items = d && d.items || [];
      const metric = c => Number(c.salesValue || 0) * 1e6 + Number(c.conversions || 0) * 1e3 + Number(c.sessions || 0);
      items.sort((a, b) => metric(b) - metric(a));
      const titles = {
        ...this.state.contentTitles
      };
      items.forEach(c => {
        titles[String(c.id)] = c.title || "";
      });
      this.setState({
        contentPicks: items,
        contentTitles: titles
      });
    });
    _defineProperty(this, "save", async activate => {
      const err = this.validate();
      if (err) {
        this.setState({
          error: err,
          confirmActivate: false
        });
        window.scrollTo(0, 0);
        return;
      }
      clearTimeout(this._saveT);
      if (this._saving) {
        try {
          await this._saving;
        } catch (e) {/* the explicit save reports its own error */}
      }
      this._dirty = false;
      this.setState({
        saving: true,
        error: null,
        issues: null,
        saveState: "saving",
        saveErr: null
      });
      try {
        const nodes = await this.buildNodes();
        const body = this.payload(nodes);
        let id = this.id();
        if (!id) {
          const created = await journeySvc().postAsync(body);
          id = created && created.id != null ? String(created.id) : null;
          if (!id) throw new Error("Create returned no id");
          this._createdId = id;
        } else {
          await journeySvc(`/${id}`).putAsync(body);
        }
        if (this._alive) this.setState({
          saveState: "saved",
          savedAt: new Date()
        });
        if (activate) {
          const v = await journeySvc(`/${id}/validate`).postAsync({});
          if (v && v.valid === false) {
            if (this._alive) this.setState({
              saving: false,
              issues: v.errors || [],
              error: "Saved as a draft. Fix these before turning it on:",
              confirmActivate: false,
              createdHere: this.state.createdHere || !this.id()
            });
            if (!this.id()) this.props.history.replace(`/Email/journeys/edit/${id}`);
            window.scrollTo(0, 0);
            return;
          }
          await journeySvc(`/${id}/status`).putAsync({
            status: "Running"
          });
        }
        if (this._alive) this.props.history.push(`/Email/journeys/detail/${id}`);
      } catch (e) {
        const msg = errText(e, "Save failed: try again.");
        let issues = null;
        try {
          const b = JSON.parse(e.message);
          if (Array.isArray(b.errors)) issues = b.errors;
        } catch (x) {/* raw */}
        if (this._alive) this.setState({
          saving: false,
          error: msg,
          issues,
          confirmActivate: false,
          saveState: "error",
          saveErr: msg
        });
        window.scrollTo(0, 0);
      }
    });
    _defineProperty(this, "pause", async () => {
      this.setState({
        pausing: true,
        error: null
      });
      try {
        await journeySvc(`/${this.id()}/status`).putAsync({
          status: "Paused"
        });
        const fresh = await journeySvc(`/${this.id()}`).getAsync();
        if (this._alive) this.setState({
          pausing: false,
          pausedHere: true,
          status: fresh && fresh.status || "Paused"
        });
      } catch (e) {
        if (this._alive) this.setState({
          pausing: false,
          error: errText(e, "Pause failed: try again.")
        });
      }
    });
    this.state = initialState(props);
    this._alive = true;
    this._dirty = false;
    this._createdId = null;
    this._loadSeq = 0;
    this._estSeq = 0;
  }
  id() {
    return this.props.params.id || null;
  }
  componentDidMount() {
    // The workspace default sender, shown when the journey has none of its own (the server sends from it).
    api("GET", "/senders").then(d => {
      const list = d && (d.items || d) || [];
      const def = Array.isArray(list) ? list.find(x => x.isDefault) || list[0] : null;
      if (def && this._alive !== false) this.setState({
        defaultSender: {
          fromName: def.fromName || "",
          fromEmail: def.fromEmail || ""
        }
      });
    }).catch(() => {});
    // "12 s ago" ticks while the draft sits saved.
    this._tick = setInterval(() => {
      if (this._alive && this.state.saveState === "saved") this.forceUpdate();
    }, 10000);
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._saveT);
    clearTimeout(this._estT);
    clearInterval(this._tick);
    // A pending edit still goes out: the request outlives the component.
    if (this._dirty && this.canAutosave()) this.autosave({
      flush: true
    });
  }

  // One Route serves /new and /edit/:id (index.jsx), so the component stays mounted across the
  // first autosave's history.replace. Any OTHER id change is a real navigation: start over.
  componentDidUpdate(prevProps, prevState) {
    const id = this.id();
    if (id !== (prevProps.params.id || null) && id !== this._createdId) {
      this._createdId = null;
      this.reset();
      return;
    }
    if (this._skipDiff) {
      this._skipDiff = false;
      return;
    }
    if (!this.state.ready || !prevState.ready) return; // the load itself is not an edit
    const st = this.state;
    if (MODEL_KEYS.some(k => st[k] !== prevState[k])) this.queueSave();
    if (REACH_KEYS.some(k => st[k] !== prevState[k])) this.queueEstimate();
  }
  reset() {
    clearTimeout(this._saveT);
    clearTimeout(this._estT);
    this._dirty = false;
    this._pending = false;
    this._skipDiff = true;
    this._loadSeq += 1;
    this.setState(initialState(this.props), () => this.load());
  }
  async load() {
    const seq = this._loadSeq;
    const live = () => this._alive && seq === this._loadSeq;
    api("GET", "/settings").then(ws => {
      if (live() && ws) this.setState({
        wsEmail: ws
      });
    }).catch(() => {});
    if (!this.id()) {
      journeySvc().getAsync().then(list => {
        if (!live()) return;
        const saved = (Array.isArray(list) ? list : []).filter(x => x.isTemplate);
        this.setState({
          savedTemplates: saved
        });
        const want = new URLSearchParams(this.props.location && this.props.location.search || "").get("template");
        if (want) {
          if (TEMPLATES.some(([k]) => k === want)) this.pickTemplate(want);else this.pickSavedTemplate(want);
        }
      }).catch(() => {});
    }
    loadBrandKit().then(b => {
      if (!live() || !b) return;
      const company = b.company || b.defaultCompany || (account.get() || {}).company || "";
      const patch = {
        brand: b
      };
      if (!this.id()) {
        patch.fromName = this.state.fromName || company;
        patch.fromEmail = this.state.fromEmail || "no-reply@plunk.io";
        patch.brandPrefilled = !!company;
      }
      this.setState(patch);
    });
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    if (!live()) return;
    emailSvc("/triggers").getAsync().then(t => live() && this.setState({
      triggers: t || null
    })).catch(() => {});
    // Lists with kind + member count (the wizard's shape): id, name, kind, total; ids as strings.
    audienceApi("GET", "").then(d => live() && this.setState({
      audienceList: normalizeLists(d)
    })).catch(() => live() && this.setState({
      audienceList: []
    }));
    if (this.id()) {
      try {
        const j = await journeySvc(`/${this.id()}`).getAsync();
        if (!live()) return;
        if (!j) {
          this.setState({
            notFound: true,
            ready: true
          });
          return;
        }
        this.setState({
          ready: true,
          ...stateFromJourney(j)
        }, () => this.queueEstimate());
      } catch (e) {
        if (live()) this.setState({
          notFound: true,
          ready: true
        });
      }
    } else {
      this.queueEstimate();
    }
  }

  // ---- autosave (2026-10-04): a named draft saves itself 900 ms after any edit ----
  isLocked() {
    return !!this.id() && this.state.status === "Running";
  }
  canAutosave() {
    return !this.isLocked() && !!(this.state.name || "").trim() && !this.state.saving;
  }
  queueSave() {
    if (this.isLocked()) return;
    this._dirty = true;
    if (!(this.state.name || "").trim()) return; // saves start once it has a name (the brand-kit prefill alone is not an edit)
    if (this.state.saveState !== "dirty") this.setState({
      saveState: "dirty"
    });
    clearTimeout(this._saveT);
    this._saveT = setTimeout(() => this.autosave(), SAVE_DEBOUNCE);
  }
  // ---- expected reach (2026-10-04): POST /journeys/estimate, debounced; silent until the endpoint is deployed ----
  queueEstimate() {
    clearTimeout(this._estT);
    this._estT = setTimeout(() => this.estimate(), ESTIMATE_DEBOUNCE);
  }
  railOf(name) {
    const t = this.state.triggers;
    if (!t || !name) return null;
    if ((t.conversions || []).some(x => x.name === name)) return "Conversion";
    if ((t.engagements || []).some(x => x.name === name)) return "Engagement";
    return null;
  }
  // ---- node edits ------------------------------------------------------
  patchNode(id, patch) {
    this.setState({
      graph: updateNode(this.state.graph, id, patch)
    });
  }
  contentLinkRow(n) {
    const st = this.state;
    if (n.containerId) this.hydrateTitle(n.containerId);
    const title = n.containerId ? st.contentTitles[String(n.containerId)] || `Content #${n.containerId}` : "";
    const open = st.contentPickerNode === n.id;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 10
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: "var(--plk-text-muted)",
        textTransform: "uppercase",
        letterSpacing: ".04em",
        marginBottom: 4
      }
    }, "Content Hub link"), n.containerId ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("a", {
      className: "cust-stage",
      style: {
        background: "var(--plk-info-soft)",
        color: "var(--plk-info-text)"
      }
    }, title), /*#__PURE__*/React.createElement("a", {
      className: "territory-toggle",
      style: {
        cursor: "pointer"
      },
      onClick: () => this.unlinkNodeContainer(n)
    }, "Unlink"), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "This step's clicks flow into this content's funnel.")) : /*#__PURE__*/React.createElement("a", {
      className: "territory-toggle",
      style: {
        cursor: "pointer"
      },
      onClick: () => this.toggleContentPicker(n.id)
    }, open ? "Close" : "Link recommended content →"), open && !n.containerId && /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px dashed var(--plk-border)",
        borderRadius: 8,
        padding: "10px 12px",
        marginTop: 8,
        maxWidth: 620
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px"
      }
    }, "Your best-performing content this quarter: link one so this step contributes to its funnel, and seed the body from its email copy."), /*#__PURE__*/React.createElement(ContentPickList, {
      items: st.contentPicks,
      onPick: c => this.pickNodeContainer(n, c)
    })));
  }
  setDesign(id, design) {
    // Keep node.designJson in step so the tree's "composed / no content yet"
    // subtitle reflects the unsaved design (buildNodes re-serializes on save).
    const hasSections = !!(design && (design.sections || []).length);
    this.setState({
      designs: {
        ...this.state.designs,
        [id]: design
      },
      graph: updateNode(this.state.graph, id, {
        designJson: hasSections ? JSON.stringify(design) : null
      })
    });
  }
  bodyMode(id) {
    return this.state.bodyModes[id] || "compose";
  }

  // ---- per-node Content Hub link (CONTENT_HUB_NEXT §6) ----
  toggleContentPicker(nodeId) {
    const open = this.state.contentPickerNode === nodeId ? null : nodeId;
    this.setState({
      contentPickerNode: open
    });
    if (open && this.state.contentPicks == null) this.loadContentPicks();
  }
  pickNodeContainer(n, c) {
    this.patchNode(n.id, {
      containerId: c.id
    });
    this.setState({
      contentPickerNode: null,
      contentTitles: {
        ...this.state.contentTitles,
        [String(c.id)]: c.title || ""
      }
    });
    // Seed the node body from the container's email variant only when empty.
    const emptyBody = this.bodyMode(n.id) === "compose" ? !(this.state.designs[n.id] && this.state.designs[n.id].sections || []).length : !(n.emailBody || "").trim();
    if (!emptyBody) return;
    contentSvc(`/containers/detail/${c.id}`).getAsync().then(d => {
      if (!this._alive || !d) return;
      const email = (d.variants || []).find(v => v.channel === "email" && (v.body || "").trim());
      if (email) {
        this.setState({
          bodyModes: {
            ...this.state.bodyModes,
            [n.id]: "html"
          }
        });
        this.patchNode(n.id, {
          emailBody: email.body
        });
      }
    }).catch(() => {});
  }
  unlinkNodeContainer(n) {
    this.patchNode(n.id, {
      containerId: null
    });
  }
  // Hydrate a linked container's title on an opened existing journey (node carries
  // the id but not the title).
  hydrateTitle(id) {
    const key = String(id);
    if (this.state.contentTitles[key] !== undefined) return;
    this.setState({
      contentTitles: {
        ...this.state.contentTitles,
        [key]: ""
      }
    });
    contentSvc(`/containers/detail/${id}`).getAsync().then(d => {
      if (this._alive && d && d.container) this.setState({
        contentTitles: {
          ...this.state.contentTitles,
          [key]: d.container.title || ""
        }
      });
    }).catch(() => {});
  }

  // ---- save --------------------------------------------------------------
  validate() {
    const st = this.state;
    if (!st.name.trim()) return "Give the journey a name.";
    if (st.kind === "custom" && !st.eventName.trim()) return "Pick the event that starts this journey.";
    if (!st.fromEmail.trim() || !/.+@.+\..+/.test(st.fromEmail)) return "Add a valid From email under Sending.";
    if (st.goalKind === "eventOccurred" && !st.goalEvent.trim()) return "Pick the event that ends the journey, under When they stop.";
    return null;
  }
  async buildNodes(opts) {
    const st = this.state;
    const render = !(opts && opts.render === false);
    const out = [];
    for (const n of st.graph.nodes) {
      const node = {
        ...n
      };
      if (node.type === "email") {
        const design = st.designs[n.id];
        if (this.bodyMode(n.id) === "compose" && design && (design.sections || []).length) {
          node.designJson = JSON.stringify(design);
          if (render) {
            try {
              const r = await this.renderPreview(design);
              node.emailBody = r && r.html || node.emailBody || "";
            } catch (e) {
              node.emailBody = node.emailBody || "";
            }
          } else node.emailBody = node.emailBody || "";
        } else if (this.bodyMode(n.id) === "compose") {
          node.designJson = null;
          node.emailBody = node.emailBody || "";
        } else {
          node.designJson = null;
        }
      }
      out.push(node);
    }
    return out;
  }
  payload(nodes) {
    const st = this.state;
    const triggerType = st.kind === "custom" ? st.eventCategory : st.kind;
    const num = v => v !== "" && !isNaN(parseInt(v, 10)) ? parseInt(v, 10) : null;
    return {
      name: st.name.trim(),
      triggerType,
      triggerConfig: {
        eventName: st.kind === "custom" ? st.eventName.trim() : st.kind === "WelcomeSeries" ? "ContactCreated" : st.kind,
        eventCategory: st.kind === "custom" ? st.eventCategory : null,
        lookbackDays: num(st.lookbackDays),
        excludeConverted: !!st.excludeConverted,
        // CustomerLapseRisk (review N2): the level that enrolls (watch | at_risk | lapsing).
        level: st.kind === "CustomerLapseRisk" ? st.lapseLevel || "at_risk" : null
      },
      audienceInclude: st.include,
      audienceExclude: st.exclude,
      senderConfig: {
        fromName: st.fromName,
        fromEmail: st.fromEmail
      },
      graph: {
        startNodeId: st.graph.startNodeId,
        nodes,
        settings: {
          quietStartHourUtc: num(st.quietStart),
          quietEndHourUtc: num(st.quietEnd),
          quietTimezone: st.quietTz || null,
          dailyEmailCap: num(st.dailyCap),
          isTemplate: !!st.isTemplate
        }
      },
      goal: {
        kind: st.goalKind || null,
        eventName: st.goalKind === "eventOccurred" ? st.goalEvent.trim() : null
      }
    };
  }
  // ---- step map (2026-10-09 redesign): the journey as a vertical map, settings in the right panel ----
  select(id) {
    this.setState({
      selected: id,
      menu: null,
      confirmRemove: null,
      waitEdit: null
    });
  }
  addStep(slot, type) {
    const {
      graph,
      id
    } = insertNode(this.state.graph, slot, type);
    this.setState({
      graph,
      menu: null,
      selected: type === "exit" ? this.state.selected : id,
      waitEdit: type === "delay" ? id : null,
      confirmRemove: null
    });
  }
  removeStep(id) {
    const n = nodeById(this.state.graph, id);
    if (n && n.type === "branch" && this.state.confirmRemove !== id) {
      this.setState({
        confirmRemove: id
      });
      return;
    }
    const patch = {
      graph: removeNode(this.state.graph, id),
      confirmRemove: null,
      menu: null
    };
    if (this.state.selected === id) patch.selected = null;
    if (this.state.composing === id) patch.composing = null;
    if (this.state.waitEdit === id) patch.waitEdit = null;
    this.setState(patch);
  }
  openComposer(id) {
    this.setState({
      selected: id,
      composing: id,
      menu: null
    });
  }

  // Drag and drop (email / wait steps only), same rules as the old step tree.
  dragProps(n) {
    if (this.isLocked() || n.type !== "email" && n.type !== "delay") return {};
    return {
      draggable: true,
      onDragStart: e => {
        try {
          e.dataTransfer.setData("text/plain", n.id);
          e.dataTransfer.effectAllowed = "move";
        } catch (x) {/* older engines */}
        this.setState({
          dragging: n.id,
          menu: null,
          confirmRemove: null
        });
      },
      onDragEnd: () => this.setState({
        dragging: null,
        over: null
      }),
      title: "Drag to move this step"
    };
  }
  dropZone(slot) {
    const {
      dragging,
      over
    } = this.state;
    if (slot && slot.parentId === dragging) return null;
    if (sameSlot(parentSlotOf(this.state.graph, dragging), slot)) return null;
    const key = slotKey(slot);
    const on = over === key;
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-drop" + (on ? " on" : ""),
      onDragOver: e => {
        e.preventDefault();
        try {
          e.dataTransfer.dropEffect = "move";
        } catch (x) {/* noop */}
        if (!on) this.setState({
          over: key
        });
      },
      onDragLeave: () => {
        if (on) this.setState({
          over: null
        });
      },
      onDrop: e => {
        e.preventDefault();
        const id = this.state.dragging;
        this.setState({
          dragging: null,
          over: null
        });
        if (id) this.setState({
          graph: moveNode(this.state.graph, id, slot)
        });
      }
    }, on ? "Drop here" : "Move here");
  }
  addButton(slot, atEnd) {
    const st = this.state;
    if (this.isLocked()) return null;
    if (st.dragging) return this.dropZone(slot);
    const key = slotKey(slot);
    if (st.menu === key) {
      return /*#__PURE__*/React.createElement("div", {
        className: "eje-chooser"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.addStep(slot, "email")
      }, "+ Email"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.addStep(slot, "delay")
      }, "+ Wait"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.addStep(slot, "branch")
      }, "+ Split by what they did"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.addStep(slot, "tag")
      }, "+ Tag them"), atEnd ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.addStep(slot, "exit")
      }, "+ End here") : null, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "eje-chooser-x",
        "aria-label": "Close",
        onClick: () => this.setState({
          menu: null
        })
      }, "\xD7"));
    }
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-plus" + (atEnd ? " end" : ""),
      "aria-label": "Add a step",
      title: "Add a step",
      onClick: () => this.setState({
        menu: key,
        confirmRemove: null
      })
    }, "+");
  }
  removeX(n) {
    if (this.isLocked()) return null;
    const stop = e => e.stopPropagation();
    if (this.state.confirmRemove === n.id) {
      return /*#__PURE__*/React.createElement("span", {
        className: "eje-confirm-x",
        onClick: stop
      }, "Remove both paths?", /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "danger",
        onClick: () => this.removeStep(n.id)
      }, "Remove"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.setState({
          confirmRemove: null
        })
      }, "Keep"));
    }
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-x",
      "aria-label": "Remove this step",
      title: n.type === "branch" ? "Remove this split and both paths below it" : "Remove this step",
      onClick: e => {
        stop(e);
        this.removeStep(n.id);
      }
    }, "\xD7");
  }
  waitEditor(n) {
    const field = (f, unit) => /*#__PURE__*/React.createElement("label", {
      className: "eje-wait-field"
    }, /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "0",
      value: n[f] || 0,
      onChange: e => this.patchNode(n.id, {
        [f]: Math.max(0, Number(e.target.value) || 0)
      })
    }), /*#__PURE__*/React.createElement("span", null, unit));
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-wait editing",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("span", null, "Wait"), field("delayDays", "days"), field("delayHours", "hours"), field("delayMinutes", "min"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn sm",
      onClick: () => this.setState({
        waitEdit: null
      })
    }, "Done"));
  }
  nodeEl(n) {
    const st = this.state;
    const on = st.selected === n.id;
    const drag = this.dragProps(n);
    const dim = st.dragging === n.id ? " dragging" : "";
    if (n.type === "delay") {
      if (st.waitEdit === n.id && !this.isLocked()) return this.waitEditor(n);
      return /*#__PURE__*/React.createElement("div", _extends({
        className: "eje-wait" + (on ? " on" : "") + dim,
        role: "button",
        tabIndex: 0
      }, drag, {
        onClick: () => this.setState({
          selected: n.id,
          waitEdit: this.isLocked() ? null : n.id,
          menu: null
        }),
        onKeyDown: e => {
          if (e.key === "Enter") this.setState({
            selected: n.id,
            waitEdit: this.isLocked() ? null : n.id
          });
        }
      }), /*#__PURE__*/React.createElement(Icon, {
        d: ICONS.clock,
        size: 16
      }), /*#__PURE__*/React.createElement("span", null, "Wait ", /*#__PURE__*/React.createElement("b", null, waitLength(n))), this.removeX(n));
    }
    if (n.type === "exit") {
      return /*#__PURE__*/React.createElement("div", {
        className: "eje-end" + (on ? " on" : ""),
        role: "button",
        tabIndex: 0,
        onClick: () => this.select(n.id)
      }, /*#__PURE__*/React.createElement(Icon, {
        d: ICONS.flag,
        size: 15
      }), /*#__PURE__*/React.createElement("span", null, "They leave the journey here"), this.removeX(n));
    }
    if (n.type === "tag") {
      return /*#__PURE__*/React.createElement("div", {
        className: "eje-card" + (on ? " on" : "") + dim,
        role: "button",
        tabIndex: 0,
        onClick: () => this.select(n.id),
        onKeyDown: e => {
          if (e.key === "Enter") this.select(n.id);
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "eje-ico tag"
      }, /*#__PURE__*/React.createElement(Icon, {
        d: ICONS.tag
      })), /*#__PURE__*/React.createElement("div", {
        className: "eje-card-text"
      }, /*#__PURE__*/React.createElement("span", {
        className: "eje-kicker"
      }, "TAG"), /*#__PURE__*/React.createElement("b", null, n.tagAudienceId ? `Add to ${tagListName(n, st.audienceList || [])}` : "Pick a list to add them to")), this.removeX(n));
    }
    const isEmail = n.type === "email";
    const title = isEmail ? nodeTitle(n) : n.name || conditionText(n, st.audienceList || []);
    let sub = "";
    if (isEmail) {
      if (n.name && n.emailSubject) sub = n.emailSubject;else if (!n.designJson && !(n.emailBody || "").trim()) sub = "No content yet";
    } else if (n.name) sub = conditionText(n, st.audienceList || []);
    return /*#__PURE__*/React.createElement("div", _extends({
      className: "eje-card" + (on ? " on" : "") + dim,
      role: "button",
      tabIndex: 0
    }, drag, {
      onClick: () => this.select(n.id),
      onKeyDown: e => {
        if (e.key === "Enter") this.select(n.id);
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "eje-ico " + (isEmail ? "email" : "split")
    }, /*#__PURE__*/React.createElement(Icon, {
      d: isEmail ? ICONS.mail : ICONS.split
    })), /*#__PURE__*/React.createElement("div", {
      className: "eje-card-text"
    }, /*#__PURE__*/React.createElement("span", {
      className: "eje-kicker"
    }, isEmail ? "EMAIL" : "SPLIT"), /*#__PURE__*/React.createElement("b", null, title), sub ? /*#__PURE__*/React.createElement("span", {
      className: "eje-card-sub"
    }, sub) : null), isEmail ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn sm",
      onClick: e => {
        e.stopPropagation();
        this.openComposer(n.id);
      }
    }, this.isLocked() ? "View email" : "Edit email") : null, this.removeX(n));
  }
  path(slot, depth) {
    const g = this.state.graph;
    const n = nodeById(g, slotTarget(g, slot));
    const editable = !this.isLocked();
    if (!n) {
      if (!editable) return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: "eje-line"
      }), /*#__PURE__*/React.createElement("span", {
        className: "eje-finish"
      }, "Then they finish"));
      const add = this.addButton(slot, true);
      return add ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: "eje-line"
      }), add) : null;
    }
    if (depth > 60) return null;
    const between = editable ? this.addButton(slot, false) : null;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "eje-line"
    }), between ? /*#__PURE__*/React.createElement(React.Fragment, null, between, /*#__PURE__*/React.createElement("span", {
      className: "eje-line"
    })) : null, this.nodeEl(n), n.type === "branch" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "eje-line"
    }), /*#__PURE__*/React.createElement("div", {
      className: "eje-branch"
    }, [["yes", "If yes"], ["no", "If not"]].map(([edge, label]) => /*#__PURE__*/React.createElement("div", {
      key: edge,
      className: "eje-branch-col"
    }, /*#__PURE__*/React.createElement("span", {
      className: "eje-branch-label " + edge
    }, label), this.path({
      parentId: n.id,
      edge
    }, depth + 1))))) : n.type === "exit" ? null : this.path({
      parentId: n.id,
      edge: "next"
    }, depth + 1));
  }
  weekly() {
    const e = this.state.estimate;
    if (!e || this.state.estimateHidden) return null;
    const days = Number(e.windowDays) || REACH_WINDOW_DAYS;
    return Math.round((Number(e.eligible) || 0) * 7 / days);
  }
  startLabel() {
    const st = this.state;
    const label = plainTrigger(st.kind)[0];
    if (st.kind === "custom") return st.eventName.trim() ? `${label}: ${st.eventName.trim()}` : label;
    if (st.kind === "CustomerLapseRisk") return `${label} (${(LAPSE_PLAIN[st.lapseLevel] || LAPSE_PLAIN.at_risk)[0].toLowerCase()})`;
    return label;
  }
  renderMap() {
    const st = this.state;
    const locked = this.isLocked();
    const editing = !!this.id();
    const weekly = this.weekly();
    const startOn = !st.selected || !nodeById(st.graph, st.selected);
    let note = null;
    if (locked) note = "This journey is running. Pause it to make changes. People in it keep their place.";else if (editing && st.status === "Paused") note = "People already in the journey keep their place. Changes apply to their next step.";
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-flow"
    }, note ? /*#__PURE__*/React.createElement("span", {
      className: "eje-note"
    }, note) : null, /*#__PURE__*/React.createElement("div", {
      className: "eje-card start" + (startOn ? " on" : ""),
      role: "button",
      tabIndex: 0,
      onClick: () => this.select(null),
      onKeyDown: e => {
        if (e.key === "Enter") this.select(null);
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "eje-ico start"
    }, /*#__PURE__*/React.createElement(Icon, {
      d: ICONS.bolt
    })), /*#__PURE__*/React.createElement("div", {
      className: "eje-card-text"
    }, /*#__PURE__*/React.createElement("span", {
      className: "eje-kicker"
    }, "STARTS WHEN"), /*#__PURE__*/React.createElement("b", null, this.startLabel())), weekly != null ? /*#__PURE__*/React.createElement("span", {
      className: "eje-card-meta"
    }, "about ", weekly.toLocaleString("en-US"), " a week") : st.estimating ? /*#__PURE__*/React.createElement("span", {
      className: "eje-card-meta"
    }, "counting\u2026") : null), this.path(null, 0), !st.graph.nodes.length && !locked ? /*#__PURE__*/React.createElement("span", {
      className: "eje-first"
    }, "Add the first step: usually an email.") : null, isSingleJourney(st.graph) && !st.isTemplate ? /*#__PURE__*/React.createElement("span", {
      className: "eje-first"
    }, "Sends one email when this happens.", locked ? "" : " Add a step to turn it into a journey.") : null, (!editing || st.createdHere) && !locked ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link eje-restart",
      onClick: () => this.setState({
        showPicker: true,
        composing: null
      })
    }, "Start from a different template") : null);
  }
  renderPicker() {
    const st = this.state;
    const hasSteps = st.graph.nodes.length > 0;
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-picker"
    }, /*#__PURE__*/React.createElement("h2", null, "Pick a starting point"), /*#__PURE__*/React.createElement("p", null, "Every step can be changed afterwards.", hasSteps ? " Picking one replaces the steps you have now." : ""), /*#__PURE__*/React.createElement("div", {
      className: "eje-picker-grid"
    }, TEMPLATES.map(([k, l, d, steps]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "eje-tpl" + (st.template === k ? " on" : ""),
      onClick: () => this.pickTemplate(k)
    }, /*#__PURE__*/React.createElement("b", null, l), /*#__PURE__*/React.createElement("span", null, d), steps ? /*#__PURE__*/React.createElement("span", {
      className: "eje-tpl-steps"
    }, steps) : null))), st.savedTemplates && st.savedTemplates.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h3", null, "Your templates"), /*#__PURE__*/React.createElement("div", {
      className: "eje-picker-grid"
    }, st.savedTemplates.map(t => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: t.id,
      className: "eje-tpl" + (st.template === `saved:${t.id}` ? " on" : ""),
      onClick: () => this.pickSavedTemplate(t.id)
    }, /*#__PURE__*/React.createElement("b", null, t.name), /*#__PURE__*/React.createElement("span", null, "Starts when: ", plainTrigger(t.triggerType, triggerText(t))[0]))))) : null, st.showPicker ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link",
      onClick: () => this.setState({
        showPicker: false
      })
    }, "Keep my current steps") : null);
  }
  renderComposer(n) {
    const st = this.state;
    const locked = this.isLocked();
    const hasDesign = !!st.designs[n.id];
    let mode = this.bodyMode(n.id);
    // Read-only with no design behind it: Design has nothing to show, so show what people receive.
    if (locked && mode === "compose" && !hasDesign) mode = "preview";
    const design = st.designs[n.id] || emptyDesign();
    const views = [["preview", "Preview"], ["compose", "Design"], ["html", "HTML"]];
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-compose"
    }, /*#__PURE__*/React.createElement("div", {
      className: "eje-compose-head"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn",
      onClick: () => this.setState({
        composing: null
      })
    }, /*#__PURE__*/React.createElement(Icon, {
      d: ICONS.back,
      size: 16
    }), " Back to the journey"), /*#__PURE__*/React.createElement("b", null, nodeTitle(n)), /*#__PURE__*/React.createElement("span", {
      className: "eje-grow"
    }), /*#__PURE__*/React.createElement("div", {
      className: "eje-seg"
    }, views.map(([v, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: v,
      className: mode === v ? "on" : "",
      onClick: () => this.setState({
        bodyModes: {
          ...st.bodyModes,
          [n.id]: v
        }
      })
    }, l)))), locked ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "Read only while the journey is running. Pause it to make changes.") : null, mode === "compose" && !hasDesign && n.emailBody ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "This email was written in HTML, so it has no design yet.", locked ? "" : " Pick a layout below to rebuild it with blocks; the HTML version is replaced when you do.") : null, mode === "preview" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-preview-frame"
    }, n.emailBody ? /*#__PURE__*/React.createElement("iframe", {
      title: `Preview of ${nodeTitle(n)}`,
      sandbox: "",
      srcDoc: n.emailBody
    }) : /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "Nothing to preview yet. Build it in Design or paste HTML.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-merge"
    }, /*#__PURE__*/React.createElement("span", null, "Personalize (click to copy):"), MERGE_CHIPS.map(([chip, tip]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: chip,
      className: "eje-merge-chip",
      title: tip,
      onClick: () => {
        try {
          navigator.clipboard.writeText(chip);
        } catch (e) {/* noop */}
      }
    }, chip))), /*#__PURE__*/React.createElement("fieldset", {
      className: "eje-fieldset",
      disabled: locked
    }, mode === "compose" ? /*#__PURE__*/React.createElement(Composer, {
      key: n.id,
      design: design,
      brand: st.brand,
      kind: st.kind === "custom" ? "custom" : st.kind,
      triggerType: st.kind === "custom" ? st.eventCategory : st.kind,
      onChange: d => this.setDesign(n.id, d),
      onPickStarter: k => this.setDesign(n.id, starterDesign(k)),
      renderPreview: this.renderPreview,
      onSendTest: () => this.sendTest(n),
      testBusy: st.testBusy === n.id,
      testNote: st.testNote && st.testNote.id === n.id ? st.testNote : null
    }) : /*#__PURE__*/React.createElement("textarea", {
      className: "eje-html",
      value: n.emailBody || "",
      onChange: e => this.patchNode(n.id, {
        emailBody: e.target.value
      }),
      placeholder: "Paste your email HTML. If it has no unsubscribe link, we add one at send time."
    }))));
  }

  // ---- right panel ----
  listChip(id, onRemove) {
    const l = (this.state.audienceList || []).find(x => x.id === id);
    return /*#__PURE__*/React.createElement("span", {
      key: id,
      className: "eje-chip"
    }, l ? `${l.name}${l.kind === "dynamic" ? " (updates itself)" : ""}${l.total ? ` · ${l.total.toLocaleString("en-US")}` : ""}` : `List ${id}`, /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": "Remove",
      title: "Remove",
      onClick: onRemove
    }, "\xD7"));
  }
  audienceLists(field, emptyText) {
    const st = this.state;
    const selected = st[field] || [];
    const chosen = (st.include || []).concat(st.exclude || []);
    const picking = st.picking === field;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-chips"
    }, selected.map(id => this.listChip(id, () => this.setState({
      [field]: selected.filter(x => x !== id)
    }))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-add-list",
      onClick: () => this.setState({
        picking: picking ? null : field
      })
    }, "+ Add a list"), !selected.length ? /*#__PURE__*/React.createElement("span", {
      className: "eje-muted"
    }, emptyText) : null), picking ? /*#__PURE__*/React.createElement(JourneyListPicker, {
      lists: st.audienceList,
      chosen: chosen,
      onPick: id => this.setState({
        [field]: selected.concat([String(id)]),
        picking: null
      }),
      onClose: () => this.setState({
        picking: null
      })
    }) : null);
  }
  reachBox(consentNote) {
    const st = this.state;
    const e = st.estimate;
    let head, text;
    if (st.estimateHidden) {
      head = "How many people";
      text = "We'll show how many people this reaches after the next update.";
    } else if (!e) {
      head = "How many people";
      text = st.estimating ? "Counting…" : st.estimateErr ? st.estimateErr : st.kind === "custom" && !st.eventName.trim() ? "Pick the event to see how many people this reaches." : "Pick what starts it to see how many people it reaches.";
    } else {
      const days = Number(e.windowDays) || REACH_WINDOW_DAYS;
      const n = Number(e.eligible) || 0;
      const before = Number(e.beforeAudience) || 0;
      const fmt = v => v.toLocaleString("en-US");
      head = days === 7 ? `About ${fmt(n)} ${n === 1 ? "person" : "people"} a week` : `About ${fmt(n)} ${n === 1 ? "person" : "people"} in ${days} days`;
      if (before === 0 && n === 0) text = `No one matched in the last ${days} days. Check what starts it.`;else if (n === 0) text = `${fmt(before)} matched in the last ${days} days, but none are in your lists.`;else {
        const names = (Array.isArray(e.sample) ? e.sample : []).map(x => x && (x.name || x.email) || "").filter(Boolean).slice(0, 3);
        text = `That's how many matched in the last ${days} days${before !== n ? ` (${fmt(before)} before your lists)` : ""}.`;
        if (names.length) text += ` For example ${names.join(", ")}.`;
      }
      text += " Each person goes through once; anyone who unsubscribes stops.";
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-reach" + (st.estimating ? " pending" : "")
    }, /*#__PURE__*/React.createElement("b", null, head), /*#__PURE__*/React.createElement("span", null, text, consentNote));
  }
  eventPicker(value, onPick, styles) {
    // The §283 picker: live trigger catalog grouped by rail, free text for
    // events the site hasn't emitted yet.
    const triggers = this.state.triggers || {};
    const options = [["", "Choose an event…"], ...(triggers.conversions || []).map(t => [t.name, t.type ? `${t.name} (${t.type}): conversion` : `${t.name}: conversion`]), ...(triggers.engagements || []).map(t => [t.name, `${t.name}: engagement`]), ["__custom", "Type a different event…"]];
    const dynamic = (triggers.conversions || []).length + (triggers.engagements || []).length > 0;
    const free = !!value && dynamic && !this.railOf(value);
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-fields"
    }, dynamic ? /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: styles.label
    }, "Event"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: free ? "__custom" : value,
      options: options,
      onChange: v => onPick(v === "__custom" ? " " : v, this.railOf(v))
    })) : null, free || !dynamic ? /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: styles.label
    }, "Event name"), /*#__PURE__*/React.createElement("input", {
      style: styles.input,
      value: value.trim(),
      placeholder: "Exact name from your site tracking",
      onChange: e => onPick(e.target.value || " ", null)
    })) : null);
  }
  startPanel(ctx) {
    const st = this.state;
    const {
      styles,
      hourOptions,
      wsq,
      wsCap,
      consentNote,
      goalWarn,
      hh
    } = ctx;
    const {
      label,
      input
    } = styles;
    const choices = triggerChoices();
    const hint = plainTrigger(st.kind)[1];
    const quietCustom = st.quietOpen || st.quietStart !== "" || st.quietEnd !== "" || st.quietTz !== "";
    const goalOptions = GOAL_KINDS.map(([k, l]) => [k, ...(GOAL_PLAIN[k] || [l, ""])]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h2", {
      className: "eje-panel-title"
    }, "Starts when"), /*#__PURE__*/React.createElement("div", {
      className: "eje-choices"
    }, choices.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "eje-choice" + (st.kind === k ? " on" : "") + (k === "custom" && choices.length % 2 ? " wide" : ""),
      onClick: () => this.setState({
        kind: k
      })
    }, l))), hint ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, hint) : null, st.kind === "custom" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-group"
    }, this.eventPicker(st.eventName, (v, rail) => this.setState({
      eventName: v,
      eventCategory: rail || st.eventCategory
    }), styles), st.eventName.trim() && !this.railOf(st.eventName.trim()) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "What kind of event is it?"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: st.eventCategory,
      options: [["Engagement", "Something they did (engagement)"], ["Conversion", "A purchase or goal (conversion)"]],
      onChange: v => this.setState({
        eventCategory: v
      })
    })), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint warn"
    }, "We haven't seen \"", st.eventName.trim(), "\" on your site yet. The journey will wait for it. If the kind is wrong it never starts.")) : null, /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Count events from the last (days)"), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      style: {
        ...input,
        width: 110
      },
      value: st.lookbackDays,
      placeholder: "7",
      onChange: e => this.setState({
        lookbackDays: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "eje-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: st.excludeConverted,
      onChange: e => this.setState({
        excludeConverted: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", null, "Skip people who already converted"))) : null, st.kind === "CustomerLapseRisk" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Start when they reach"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: st.lapseLevel || "at_risk",
      options: LAPSE_RISK_LEVELS.map(([k, l]) => [k, (LAPSE_PLAIN[k] || [l])[0]]),
      onChange: v => this.setState({
        lapseLevel: v
      })
    })), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, (LAPSE_PLAIN[st.lapseLevel || "at_risk"] || [])[1] || "", " Each customer starts once, the first time they reach this point or further.")) : null, /*#__PURE__*/React.createElement("section", {
      className: "eje-section"
    }, /*#__PURE__*/React.createElement("h3", null, "Only people in"), this.audienceLists("include", "Anyone, for now"), /*#__PURE__*/React.createElement("h3", {
      className: "eje-h3-sub"
    }, "Leave out people in"), this.audienceLists("exclude", "No one"), st.audienceList && !st.audienceList.length ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "You don't have any lists yet. ", /*#__PURE__*/React.createElement(Link, {
      to: "/Audiences/this_month"
    }, "Make a list")) : null), this.reachBox(consentNote), /*#__PURE__*/React.createElement("section", {
      className: "eje-section"
    }, /*#__PURE__*/React.createElement("h3", null, "Sending"), /*#__PURE__*/React.createElement("div", {
      className: "eje-sender"
    }, st.fromEmail ? /*#__PURE__*/React.createElement("span", null, "From ", st.fromName ? `${st.fromName} ` : "", "<", st.fromEmail, ">") : st.defaultSender && st.defaultSender.fromEmail ? /*#__PURE__*/React.createElement("span", null, "From ", st.defaultSender.fromName ? `${st.defaultSender.fromName} ` : "", "<", st.defaultSender.fromEmail, ">, your default") : /*#__PURE__*/React.createElement("span", null, "From your default sender"), " · ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link",
      onClick: () => this.setState({
        senderOpen: !st.senderOpen
      })
    }, st.senderOpen ? "Done" : "Change")), st.senderOpen ? /*#__PURE__*/React.createElement("div", {
      className: "eje-group"
    }, /*#__PURE__*/React.createElement(SenderPicker, {
      fromName: st.fromName,
      fromEmail: st.fromEmail,
      inputStyle: input,
      labelStyle: label,
      onPick: p => this.setState({
        fromName: p.fromName,
        fromEmail: p.fromEmail
      })
    })) : null, /*#__PURE__*/React.createElement("label", {
      className: "eje-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: quietCustom,
      onChange: e => e.target.checked ? this.setState({
        quietOpen: true
      }) : this.setState({
        quietOpen: false,
        quietStart: "",
        quietEnd: "",
        quietTz: ""
      })
    }), /*#__PURE__*/React.createElement("span", null, "Use different quiet hours for this journey")), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, wsq ? `Otherwise your workspace quiet hours apply (${hh(wsq.startHour)} to ${hh(wsq.endHour)} ${wsq.timezone || "UTC"}).` : "Your workspace doesn't hold emails overnight right now.", st.wsEmail ? "" : " Loading your workspace settings…"), quietCustom ? /*#__PURE__*/React.createElement("div", {
      className: "eje-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Hold emails from"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: st.quietStart,
      options: hourOptions,
      onChange: v => this.setState({
        quietStart: v
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Until"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: st.quietEnd,
      options: hourOptions,
      onChange: v => this.setState({
        quietEnd: v
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Time zone"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: st.quietTz,
      options: TZ_OPTIONS,
      onChange: v => this.setState({
        quietTz: v
      })
    }))) : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-disclose",
      "aria-expanded": !!st.showMore,
      onClick: () => this.setState({
        showMore: !st.showMore
      })
    }, st.showMore ? "▾" : "▸", " Email limits and templates"), st.showMore ? /*#__PURE__*/React.createElement("div", {
      className: "eje-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Most emails one person gets in a day"), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "0",
      style: {
        ...input,
        width: 200
      },
      value: st.dailyCap,
      placeholder: `Workspace setting (${wsCap})`,
      onChange: e => this.setState({
        dailyCap: e.target.value
      })
    })), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "Counts every journey email to that person. Anything over the limit waits for the next day."), /*#__PURE__*/React.createElement("label", {
      className: "eje-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!st.isTemplate,
      onChange: e => this.setState({
        isTemplate: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", null, "Keep as a template for new journeys (it stays a draft)"))) : null), /*#__PURE__*/React.createElement("section", {
      className: "eje-section"
    }, /*#__PURE__*/React.createElement("h3", null, "When they stop"), /*#__PURE__*/React.createElement("div", {
      className: "eje-options"
    }, goalOptions.map(([k, l, d]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "eje-option" + (st.goalKind === k ? " on" : ""),
      onClick: () => this.setState({
        goalKind: k
      })
    }, /*#__PURE__*/React.createElement("b", null, l), d ? /*#__PURE__*/React.createElement("span", null, d) : null))), st.goalKind === "eventOccurred" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-group"
    }, this.eventPicker(st.goalEvent, v => this.setState({
      goalEvent: v
    }), styles)) : null, goalWarn ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint warn"
    }, "People who buy partway through will still get the later emails.", " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link",
      onClick: () => this.setState({
        goalKind: "conversion"
      })
    }, "Stop when they convert")) : null));
  }
  emailPanel(n, styles) {
    const st = this.state;
    const {
      label,
      input
    } = styles;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-panel-head"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "eje-panel-title"
    }, "Email"), this.isLocked() ? null : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link danger",
      onClick: () => this.removeStep(n.id)
    }, "Remove")), /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Subject"), /*#__PURE__*/React.createElement("input", {
      style: input,
      value: n.emailSubject || "",
      placeholder: "e.g. Welcome, {{FirstName}}",
      onChange: e => this.patchNode(n.id, {
        emailSubject: e.target.value
      })
    }), /*#__PURE__*/React.createElement(SubjectSuggest, {
      kind: st.kind === "custom" ? "custom" : st.kind,
      triggerType: st.kind === "custom" ? st.eventCategory : st.kind,
      subject: n.emailSubject || "",
      onUse: p => this.patchNode(n.id, {
        emailSubject: p.subject || n.emailSubject || ""
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Name on the map"), /*#__PURE__*/React.createElement("input", {
      style: input,
      value: n.name || "",
      placeholder: "e.g. Welcome",
      onChange: e => this.patchNode(n.id, {
        name: e.target.value
      })
    })), this.isLocked() ? null : /*#__PURE__*/React.createElement("div", {
      className: "eje-row"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn primary",
      onClick: () => this.openComposer(n.id)
    }, "Edit email"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn",
      disabled: st.testBusy === n.id,
      onClick: () => this.sendTest(n),
      title: "Sends this email to you with sample data. Nothing is saved and no one else gets it."
    }, st.testBusy === n.id ? "Sending…" : "Send me a test")), st.testNote && st.testNote.id === n.id ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint" + (st.testNote.kind === "error" ? " danger" : "")
    }, st.testNote.text) : null, /*#__PURE__*/React.createElement("section", {
      className: "eje-section"
    }, this.contentLinkRow(n)));
  }
  waitPanel(n, styles) {
    const {
      label,
      input
    } = styles;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-panel-head"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "eje-panel-title"
    }, "Wait"), this.isLocked() ? null : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link danger",
      onClick: () => this.removeStep(n.id)
    }, "Remove")), /*#__PURE__*/React.createElement("div", {
      className: "eje-fields"
    }, [["delayDays", "Days"], ["delayHours", "Hours"], ["delayMinutes", "Minutes"]].map(([f, l]) => /*#__PURE__*/React.createElement("div", {
      key: f,
      className: "eje-field narrow"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, l), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "0",
      style: input,
      value: n[f] || 0,
      onChange: e => this.patchNode(n.id, {
        [f]: Math.max(0, Number(e.target.value) || 0)
      })
    })))), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "before the next step. Quiet hours and the daily limit can make the next email later, never earlier."));
  }
  tagPanel(n, styles) {
    const st = this.state;
    const {
      label
    } = styles;
    const lists = st.audienceList;
    const pick = v => {
      const a = (lists || []).find(x => String(x.id) === String(v));
      this.patchNode(n.id, {
        tagAudienceId: v ? String(v) : "",
        tagName: a ? a.name : ""
      });
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-panel-head"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "eje-panel-title"
    }, "Tag them"), this.isLocked() ? null : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link danger",
      onClick: () => this.removeStep(n.id)
    }, "Remove")), /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Add them to this list"), lists == null ? /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "Loading your lists\u2026") : lists.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: String(n.tagAudienceId || ""),
      options: [["", "Choose a list…"], ...lists.map(a => [a.id, a.name])],
      onChange: pick
    }) : /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "You don't have any lists yet. ", /*#__PURE__*/React.createElement(Link, {
      to: "/Audiences/this_month"
    }, "Make a list"))), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "Everyone who reaches this step is added to the list, then moves straight on to the next step. People already on the list stay on it."));
  }
  branchPanel(n, styles) {
    const st = this.state;
    const {
      label,
      input
    } = styles;
    const c = n.condition || {};
    const setC = patch => this.patchNode(n.id, {
      condition: {
        ...c,
        ...patch
      }
    });
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "eje-panel-head"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "eje-panel-title"
    }, "Split by what they did"), this.isLocked() ? null : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link danger",
      onClick: () => this.removeStep(n.id)
    }, "Remove")), /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Check if they\u2026"), /*#__PURE__*/React.createElement("div", {
      className: "eje-choices"
    }, CONDITION_KINDS.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "eje-choice" + (c.kind === k ? " on" : ""),
      onClick: () => setC({
        kind: k
      })
    }, CONDITION_PLAIN[k] || l))), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, (CONDITION_KINDS.find(([k]) => k === c.kind) || [])[2]), c.kind === "eventOccurred" ? this.eventPicker(c.eventName || "", v => setC({
      eventName: v
    }), styles) : null, c.kind === "inAudience" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "List"), st.audienceList && st.audienceList.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "100%",
      value: String(c.audienceId || ""),
      options: [["", "Choose a list…"], ...st.audienceList.map(a => [a.id, a.name])],
      onChange: v => setC({
        audienceId: v
      })
    }) : /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "You don't have any lists yet.")) : null, c.kind === "lifecycleStage" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Stage"), /*#__PURE__*/React.createElement("div", {
      className: "eje-chips"
    }, ["lead", "prospect", "customer", "repeat", "lapsed"].map(k => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "eje-choice sm" + (String(c.value || "") === k ? " on" : ""),
      onClick: () => setC({
        value: k
      })
    }, k))), /*#__PURE__*/React.createElement("input", {
      style: input,
      value: c.value || "",
      placeholder: "or type the stage as it appears on the customer record",
      onChange: e => setC({
        value: e.target.value
      })
    }), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "Read from their customer record. People without one take the \"If not\" path.")) : null, c.kind === "assignedRep" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Rep id"), /*#__PURE__*/React.createElement("input", {
      style: input,
      value: c.value || "",
      placeholder: "The rep's id from the customer record",
      onChange: e => setC({
        value: e.target.value
      })
    }), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "People without a customer record or a rep take the \"If not\" path.")) : null, c.kind === "converted" || c.kind === "eventOccurred" ? /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Only count the last (days)"), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      style: {
        ...input,
        width: 110
      },
      value: c.windowDays || "",
      placeholder: "Since they started",
      onChange: e => setC({
        windowDays: e.target.value ? Math.max(1, Number(e.target.value) || 1) : null
      })
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: "eje-field"
    }, /*#__PURE__*/React.createElement("label", {
      style: label
    }, "Name on the map (optional)"), /*#__PURE__*/React.createElement("input", {
      style: input,
      value: n.name || "",
      placeholder: "e.g. Opened the welcome email?",
      onChange: e => this.patchNode(n.id, {
        name: e.target.value
      })
    })), /*#__PURE__*/React.createElement("p", {
      className: "eje-hint"
    }, "The two paths never join back up. Each one ends on its own."));
  }
  renderPanel(ctx) {
    const st = this.state;
    const locked = this.isLocked();
    const n = st.selected ? nodeById(st.graph, st.selected) : null;
    let body;
    if (!n) body = this.startPanel(ctx);else if (n.type === "email") body = this.emailPanel(n, ctx.styles);else if (n.type === "delay") body = this.waitPanel(n, ctx.styles);else if (n.type === "branch") body = this.branchPanel(n, ctx.styles);else if (n.type === "tag") body = this.tagPanel(n, ctx.styles);else {
      body = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "eje-panel-head"
      }, /*#__PURE__*/React.createElement("h2", {
        className: "eje-panel-title"
      }, "End"), locked ? null : /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "eje-link danger",
        onClick: () => this.removeStep(n.id)
      }, "Remove")), /*#__PURE__*/React.createElement("p", {
        className: "eje-hint"
      }, "People who get here leave the journey. A path that simply stops does the same; this just makes it clear."));
    }
    return /*#__PURE__*/React.createElement("aside", {
      className: "eje-panel"
    }, n ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-link eje-panel-back",
      onClick: () => this.select(null)
    }, "\u2039 Journey settings") : null, /*#__PURE__*/React.createElement("fieldset", {
      className: "eje-fieldset",
      disabled: locked
    }, body), locked && n && n.type === "email" ?
    /*#__PURE__*/
    // Outside the disabled fieldset: looking at the email and testing it are fine while it runs.
    React.createElement("div", {
      className: "eje-row"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn primary",
      onClick: () => this.openComposer(n.id)
    }, "View email"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn",
      disabled: st.testBusy === n.id,
      onClick: () => this.sendTest(n)
    }, st.testBusy === n.id ? "Sending…" : "Send me a test")) : null, !n ? /*#__PURE__*/React.createElement("section", {
      className: "eje-section"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-disclose",
      "aria-expanded": !!st.showHow,
      onClick: () => this.setState({
        showHow: !st.showHow
      })
    }, st.showHow ? "▾" : "▸", " How journeys work"), st.showHow ? /*#__PURE__*/React.createElement("ul", {
      className: "eje-how"
    }, /*#__PURE__*/React.createElement("li", null, "We look for new people every 5 minutes. Built-in starts look back 7 days; for \"Something else on your site\" you choose how far back."), /*#__PURE__*/React.createElement("li", null, "Each person goes through a journey once. People who unsubscribed never start, and anyone who unsubscribes partway leaves."), /*#__PURE__*/React.createElement("li", null, "Waits start counting when someone enters. Emails due during quiet hours wait until they end."), /*#__PURE__*/React.createElement("li", null, "The daily limit counts journey emails to a person across your workspace. Anything over it waits."), /*#__PURE__*/React.createElement("li", null, "A split sends people down one of two paths. The paths don't join back up."), /*#__PURE__*/React.createElement("li", null, "Every email gets your brand header, plus an unsubscribe footer if it doesn't have one. Company name, address and website come from your ", /*#__PURE__*/React.createElement("a", {
      href: "/Settings/Brand"
    }, "brand kit"), "."), /*#__PURE__*/React.createElement("li", null, "Turning it on checks every step: emails need a subject and content, waits need a length, splits need a condition."), /*#__PURE__*/React.createElement("li", null, "While a journey runs, pause it to make changes. Everyone keeps their place.")) : null) : null);
  }
  saveIndicator(locked) {
    const st = this.state;
    let text,
      tone = "";
    if (locked) text = "Read only while running";else if (st.saveState === "saving" || st.saving) text = "Saving…";else if (st.saveState === "error") {
      text = "Couldn't save";
      tone = " danger";
    } else if (st.saveState === "dirty") text = "Unsaved changes";else if (st.saveState === "saved") {
      text = "Saved";
      tone = " ok";
    } else if (!this.id()) text = (st.name || "").trim() ? "Not saved yet" : "Name it to save";else {
      text = "Saved";
      tone = " ok";
    }
    const title = st.saveState === "error" ? st.saveErr : st.saveState === "saved" && st.savedAt ? `Saved ${agoShort(st.savedAt)}` : undefined;
    return /*#__PURE__*/React.createElement("span", {
      className: "eje-saved" + tone,
      title: title || undefined
    }, text);
  }
  statePill(locked) {
    const st = this.state;
    if (locked) return /*#__PURE__*/React.createElement("span", {
      className: "eje-pill running"
    }, "Running");
    if (st.status === "Paused") return /*#__PURE__*/React.createElement("span", {
      className: "eje-pill paused"
    }, st.pausedHere ? "Paused while you edit" : "Paused");
    if (st.isTemplate) return /*#__PURE__*/React.createElement("span", {
      className: "eje-pill"
    }, "Template");
    if (st.status && st.status !== "Draft") return /*#__PURE__*/React.createElement("span", {
      className: "eje-pill"
    }, st.status);
    return /*#__PURE__*/React.createElement("span", {
      className: "eje-pill"
    }, "Draft");
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Email isn't set up for this workspace."));
    }
    const st = this.state;
    if (!st.ready) return /*#__PURE__*/React.createElement(Loader, null);
    if (st.notFound) return /*#__PURE__*/React.createElement(Error, {
      message: "This journey could not be loaded."
    });
    const editing = !!this.id();
    const locked = editing && st.status === "Running";
    const styles = {
      input: INPUT,
      label: LABEL
    };
    const wsq = st.wsEmail && st.wsEmail.quietHours && st.wsEmail.quietHours.enabled ? st.wsEmail.quietHours : null;
    const wsCap = st.wsEmail && st.wsEmail.journeyCap && st.wsEmail.journeyCap.max ? st.wsEmail.journeyCap.max : 2;
    const hh = h => `${String(h).padStart(2, "0")}:00`;
    const wsQuietLabel = wsq ? `Workspace setting (${hh(wsq.startHour)} to ${hh(wsq.endHour)} ${wsq.timezone || "UTC"})` : "Workspace setting (off)";
    const hourOptions = [["", wsQuietLabel], ...HOURS.map(h => [String(h), st.quietTz ? hh(h) : `${hh(h)} UTC · ${utcHourLocal(h)} your time`])];
    const goalWarn = !st.goalKind && wantsGoal(st);
    const consent = st.wsEmail && st.wsEmail.consentDefault ? String(st.wsEmail.consentDefault) : "";
    const consentNote = consent && !/allow/i.test(consent) ? " People without recorded consent are skipped too, per your workspace setting." : "";
    const ctx = {
      styles,
      hourOptions,
      wsq,
      wsCap,
      consentNote,
      goalWarn,
      hh
    };
    const emails = emailNodes(st.graph);
    const selectedEmail = st.selected ? emails.find(x => x.id === st.selected) : null;
    const testTarget = selectedEmail || emails[0] || null;
    const composingNode = st.composing ? nodeById(st.graph, st.composing) : null;
    const showPicker = !locked && (st.showPicker || !st.template && (!editing || st.createdHere) && !st.graph.nodes.length);
    // One email and nothing else reads as a single email; a second step makes it a journey (same engine either way).
    const single = !st.isTemplate && isSingleJourney(st.graph);
    const backTo = editing ? `/Email/journeys/detail/${this.id()}` : single ? "/Email/journeys?show=single" : "/Email/journeys";
    return /*#__PURE__*/React.createElement("div", {
      className: "eje-root"
    }, /*#__PURE__*/React.createElement("div", {
      className: "eje-top"
    }, /*#__PURE__*/React.createElement(Link, {
      className: "eje-back",
      to: backTo,
      "aria-label": editing ? "Back to the journey" : "Back to journeys",
      title: editing ? "Back to the journey" : "Back to journeys"
    }, /*#__PURE__*/React.createElement(Icon, {
      d: ICONS.back
    })), /*#__PURE__*/React.createElement("input", {
      className: "eje-name",
      value: st.name,
      placeholder: single ? "Name this email" : "Name this journey",
      "aria-label": single ? "Email name" : "Journey name",
      readOnly: locked,
      onChange: e => this.setState({
        name: e.target.value
      })
    }), single ? /*#__PURE__*/React.createElement("span", {
      className: "eje-kind"
    }, "Single email") : null, this.statePill(locked), /*#__PURE__*/React.createElement("span", {
      className: "eje-grow"
    }), st.testNote ? /*#__PURE__*/React.createElement("span", {
      className: "eje-test-note" + (st.testNote.kind === "error" ? " danger" : ""),
      title: st.testNote.text
    }, st.testNote.text) : null, this.saveIndicator(locked), testTarget ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn",
      disabled: st.testBusy === testTarget.id,
      onClick: () => this.sendTest(testTarget),
      title: `Sends "${nodeTitle(testTarget)}" to you with sample data. Nothing is saved and no one else gets it.`
    }, st.testBusy === testTarget.id ? "Sending…" : "Send me a test") : null, locked ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn primary",
      disabled: st.pausing,
      onClick: this.pause
    }, st.pausing ? "Pausing…" : "Pause to edit") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn",
      disabled: st.saving,
      onClick: () => this.save(false),
      title: "Save and go to the journey page"
    }, st.saving && !st.confirmActivate ? "Saving…" : "Save and close"), /*#__PURE__*/React.createElement("div", {
      className: "eje-activate"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn primary",
      disabled: st.saving,
      onClick: () => this.setState({
        confirmActivate: !st.confirmActivate
      })
    }, st.status === "Paused" ? "Turn back on" : "Turn on"), st.confirmActivate ? /*#__PURE__*/React.createElement("div", {
      className: "eje-confirm",
      role: "dialog"
    }, /*#__PURE__*/React.createElement("p", null, st.status === "Paused" ? "Start sending again? People in the journey pick up where they left off." : "Turn it on? New people who match start from now on."), /*#__PURE__*/React.createElement("div", {
      className: "eje-row"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn",
      disabled: st.saving,
      onClick: () => this.setState({
        confirmActivate: false
      })
    }, "Not yet"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "eje-btn primary",
      disabled: st.saving,
      onClick: () => this.save(true)
    }, st.saving ? "Turning on…" : st.status === "Paused" ? "Turn back on" : "Turn on"))) : null))), st.error ? /*#__PURE__*/React.createElement("div", {
      className: "eje-error"
    }, st.error, st.issues && st.issues.length ? /*#__PURE__*/React.createElement("ul", null, st.issues.map((x, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, x))) : null) : null, /*#__PURE__*/React.createElement("div", {
      className: "eje-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "eje-canvas"
    }, composingNode && composingNode.type === "email" ? this.renderComposer(composingNode) : showPicker ? this.renderPicker() : this.renderMap()), showPicker && !composingNode ? null : this.renderPanel(ctx)));
  }
}
const slotKey = slot => slot ? `${slot.parentId}:${slot.edge}` : "start";
function waitLength(n) {
  const t = delayText(n);
  return /^Wait /.test(t) && !/no duration/.test(t) ? t.replace(/^Wait /, "") : "(set a length)";
}
function Icon({
  d,
  size = 18
}) {
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  }, d);
}
const ICONS = {
  back: /*#__PURE__*/React.createElement("path", {
    d: "M15 18l-6-6 6-6"
  }),
  bolt: /*#__PURE__*/React.createElement("path", {
    d: "M13 2L4 14h7l-1 8 9-12h-7l1-8z"
  }),
  mail: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("rect", {
    x: "3",
    y: "5",
    width: "18",
    height: "14",
    rx: "2"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3 7l9 6 9-6"
  })),
  clock: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "9"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M12 7v5l3 2"
  })),
  split: /*#__PURE__*/React.createElement("path", {
    d: "M12 3v6M12 9l-6 6v6M12 9l6 6v6"
  }),
  flag: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
    d: "M5 21V4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5 4h11l-2 4 2 4H5"
  })),
  tag: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
    d: "M3 12V4a1 1 0 011-1h8l9 9-9 9-9-9z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "7.5",
    cy: "7.5",
    r: "1.5"
  }))
};

// Plain-language names for the existing trigger kinds (keys unchanged; unknown kinds fall back to common.js labels).
const TRIGGER_PLAIN = {
  WelcomeSeries: ["Someone signs up", "New contacts with an email address. Each person starts once, ever."],
  ProductViewed: ["Looks at products", "People who looked at products and haven't bought yet."],
  CartAbandoned: ["Leaves a cart", "People who added to their cart and didn't buy. For now this also reaches people who only looked at products."],
  CustomerLoyalty: ["Buys again", "Customers who have bought more than once."],
  CustomerLapsed: ["Hasn't bought in a while", "Customers whose last order was a while ago (90 days unless set otherwise)."],
  CustomerLapseRisk: ["Might stop buying", "Customers ordering less often than they usually do, judged against their own buying pattern."],
  custom: ["Something else on your site", "Any event your site tracks."]
};
function plainTrigger(kind, fallback) {
  if (TRIGGER_PLAIN[kind]) return TRIGGER_PLAIN[kind];
  const known = TRIGGER_KINDS.find(([k]) => k === kind);
  return known ? [known[1], known[2] || ""] : [fallback || String(kind || "Something happens"), ""];
}
function triggerChoices() {
  const out = TRIGGER_KINDS.filter(([k]) => k !== "custom").map(([k]) => [k, plainTrigger(k)[0]]);
  if (TRIGGER_KINDS.some(([k]) => k === "custom")) out.push(["custom", plainTrigger("custom")[0]]);
  return out;
}
const LAPSE_PLAIN = {
  watch: ["Slowing down", "The earliest nudge."],
  at_risk: ["At risk", "Ordering noticeably less than usual."],
  lapsing: ["About to stop", "The last call."]
};
const CONDITION_PLAIN = {
  converted: "Converted",
  openedLastEmail: "Opened the last email",
  eventOccurred: "Did something on your site",
  inAudience: "Are in a list",
  lifecycleStage: "Are at a stage",
  assignedRep: "Have a certain rep"
};
const GOAL_PLAIN = {
  "": ["After the last step", "They get every step."],
  conversion: ["As soon as they convert", "Any purchase or goal your site tracks. Checked before every step."],
  eventOccurred: ["When they do something specific", "Pick the event below."]
};
export default withRouter(EmailJourneyEditorComponent);